import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Protected Route Component
 * Wraps routes that require authentication and/or specific roles
 * Redirects to login page if not authenticated or not authorized
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {boolean} [props.requireAdmin=false] - Whether admin privileges are required
 * @param {string} [props.redirectPath='/login'] - Path to redirect to if not authenticated
 * @param {Object} props.auth - Authentication context with user info
 * @param {boolean} [props.auth.isAuthenticated] - Whether user is authenticated
 * @param {boolean} [props.auth.isAdmin] - Whether user has admin privileges
 * @param {boolean} [props.auth.loading] - Whether auth state is being loaded
 */
const ProtectedRoute = ({ 
  children, 
  requireAdmin = false, 
  redirectPath = '/login',
  auth = {}
}) => {
  const location = useLocation();
  
  // Safely destructure with defaults
  const { isAuthenticated = false, isAdmin = false, loading = false } = auth;

  // Show loading state while checking authentication
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-600">Verifying authentication...</p>
      </div>
    </div>;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // If admin access required but user is not admin, redirect to home
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and has required permissions
  return children;
};

export default ProtectedRoute;
